import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Calendar, Building2, ChevronLeft, BadgeCheck, Store } from "lucide-react";
import { api } from "@/lib/api";
import { money, catLabel, PERIODS, TYPE_LABELS, TYPE_PATHS, FALLBACK_IMG, STATUS_LABELS } from "@/lib/format";
import { BidPanel, BidHistory } from "@/components/BidPanel";
import { CompanyDetailsView } from "@/components/CompanyDetails";
import { FinancialsView } from "@/components/Financials";
import { RefCode } from "@/components/RefCode";
import { CvAnalyzer } from "@/components/CvAnalyzer";
import { ContactPanel } from "@/components/ContactPanel";

const Meta = ({ l }) => (
  <div className="flex flex-wrap gap-2 text-sm">
    <span className="bg-brand/10 text-brand font-semibold rounded-lg px-3 py-1">{catLabel(l.type, l.category)}</span>
    {l.city && <span className="bg-slate-100 rounded-lg px-3 py-1 flex items-center gap-1"><MapPin size={14} />{l.city}</span>}
    {l.year && <span className="bg-slate-100 rounded-lg px-3 py-1 flex items-center gap-1"><Calendar size={14} />موديل {l.year}</span>}
    {l.company && <span className="bg-slate-100 rounded-lg px-3 py-1 flex items-center gap-1"><Building2 size={14} />{l.company}</span>}
    {l.job_type && <span className="bg-slate-100 rounded-lg px-3 py-1">{l.job_type}</span>}
    {l.status !== "approved" && <span className="bg-amber-100 text-amber-800 rounded-lg px-3 py-1" data-testid="detail-status">{STATUS_LABELS[l.status]}</span>}
  </div>
);

const PriceBox = ({ l }) => (
  <div className="bg-white border rounded-xl p-5" data-testid="price-box">
    <div className="text-xs text-slate-500 mb-1">{l.type === "job" ? "الراتب" : l.type === "rent" ? "سعر الإيجار" : l.type === "invest" ? "المبلغ المطلوب" : "السعر"}</div>
    <div className="text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
      {l.type === "job" ? (l.salary || "حسب الخبرة") : money(l.price)}
      {l.type === "rent" && <span className="text-base text-slate-500 font-semibold mr-1">{PERIODS[l.rent_period]}</span>}
    </div>
    {l.stake_percent && <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600" data-testid="stake-percent">نسبة الحصة المعروضة: <b className="text-slate-900">{l.stake_percent}%</b></div>}
  </div>
);

export default function ListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [bids, setBids] = useState([]);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/listings/${id}`).then((r) => {
      setL(r.data);
      if (r.data.type === "auction") api.get(`/listings/${id}/bids`).then((b) => setBids(b.data));
    }).catch(() => setErr(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="py-32 text-center" data-testid="detail-not-found">الإعلان غير موجود أو بانتظار المراجعة.</div>;
  if (!l) return <div className="py-32 text-center text-slate-400">جارِ التحميل...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="listing-detail">
      <div className="text-sm text-slate-500 flex items-center gap-1 mb-5">
        <Link to={TYPE_PATHS[l.type]} className="hover:text-brand" data-testid="detail-breadcrumb">{TYPE_LABELS[l.type]}</Link><ChevronLeft size={14} /><span className="truncate">{l.title}</span>
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6 min-w-0">
          {l.type !== "job" && <img src={l.image || FALLBACK_IMG[l.type]} alt={l.title} className="w-full aspect-[16/10] object-cover rounded-xl bg-slate-100" data-testid="detail-image" />}
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="detail-title">{l.title}</h1>
            <div className="mb-4 flex items-center gap-2 text-xs text-slate-500">رقم الإعلان <RefCode code={l.ref_code} testId="detail-ref" /></div>
            <Meta l={l} />
          </div>
          <div className="bg-white border rounded-xl p-6">
            <h2 className="font-bold mb-3">التفاصيل</h2>
            <p className="text-slate-600 leading-loose whitespace-pre-line" data-testid="detail-description">{l.description || "لا يوجد وصف."}</p>
          </div>
          {l.company_details && <CompanyDetailsView c={l.company_details} />}
          <FinancialsView f={l.financials} locked={l.financials_locked} />
          {l.type === "job" && l.status === "approved" && <CvAnalyzer l={l} />}
          {l.type === "auction" && <BidHistory bids={bids} />}
        </div>
        <aside className="space-y-5 lg:sticky lg:top-28 self-start order-first lg:order-none">
          {l.type === "auction" ? (l.status === "approved" ? <BidPanel l={l} onBid={load} /> : <div className="bg-amber-50 rounded-xl p-5 text-sm">يبدأ المزاد بعد اعتماد الإدارة.</div>) : <PriceBox l={l} />}
          {l.status === "approved" && <ContactPanel l={l} onChange={load} />}
          {l.seller && (
            <Link to={`/seller/${l.seller.id}`} data-testid="seller-link" className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Store size={16} className="text-slate-500" />{l.seller.name}</span>
              {l.seller.verified ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="seller-link-verified"><BadgeCheck size={14} />موثّق</span> : <span className="text-xs text-slate-400">صفحة البائع</span>}
            </Link>
          )}
        </aside>
      </div>
    </div>
  );
}
